import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { ThreadSummary } from '@core/tellent-assistant-types';
import { ButtonComponent, IconButtonComponent, IconComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-thread-drawer',
  standalone: true,
  imports: [ButtonComponent, IconButtonComponent, IconComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { role: 'complementary' },
  template: `
    <header class="top">
      <rt-icon-button iconName="arrow-back" size="small" variant="plain" (click)="back.emit()" />
    </header>

    <rt-button
      class="new"
      variant="plain"
      size="medium"
      layout-full-width
      (click)="newThread.emit()"
    >
      <rt-icon name="edit-square" colorToken="colorIconPrimaryDefault" />
      <span class="new-label">
        {{ 'tlc.tellent_assistant.thread_drawer.new_conversation_label' | translate }}
      </span>
    </rt-button>

    <p class="label">
      {{ 'tlc.tellent_assistant.thread_drawer.history_heading' | translate }}
    </p>
    <ul>
      @for (thread of threads(); track thread.id) {
        <li>
          <rt-button
            variant="plain"
            size="medium"
            layout-full-width
            [class.active]="thread.id === activeId()"
            (click)="selectThread.emit(thread.id)"
          >
            <span class="thread-title">{{ thread.title || defaultTitle }}</span>
            <rt-icon-button
              iconName="delete"
              size="small"
              variant="plain"
              class="delete"
              [disabled]="isDeleting()(thread.id)"
              data-testid="thread-delete"
              (click)="onDeleteClick(thread.id, $event)"
            />
          </rt-button>
        </li>
      }
    </ul>

    <footer class="bottom">
      <rt-button
        class="action"
        variant="plain"
        size="medium"
        layout-full-width
        (click)="helpCenter.emit()"
      >
        <rt-icon name="help-outline" class="action-icon" />
        <span class="action-label">
          {{ 'tlc.tellent_assistant.thread_drawer.help_center_label' | translate }}
        </span>
        <rt-icon name="open-in-new" class="external" />
      </rt-button>
      <rt-button
        class="action"
        variant="plain"
        size="medium"
        layout-full-width
        (click)="intelligenceSettings.emit()"
      >
        <rt-icon name="nav-settings-outline" class="action-icon" />
        <span class="action-label">
          {{ 'tlc.tellent_assistant.thread_drawer.intelligence_settings_label' | translate }}
        </span>
        <rt-icon name="open-in-new" class="external" />
      </rt-button>
    </footer>
  `,
  styleUrls: ['./tellent-assistant-thread-drawer.component.less'],
})
export class TellentAssistantThreadDrawerComponent {
  private readonly i18n = inject(I18nService);

  protected readonly defaultTitle = this.i18n.translate(
    'tlc.tellent_assistant.thread.default_title',
  );

  public readonly threads = input.required<readonly ThreadSummary[]>();
  public readonly activeId = input<string | null>(null);
  public readonly isDeleting = input<(threadId: string) => boolean>(() => false);
  public readonly back = output<void>();
  public readonly newThread = output<void>();
  public readonly selectThread = output<string>();
  public readonly deleteThread = output<string>();
  public readonly helpCenter = output<void>();
  public readonly intelligenceSettings = output<void>();

  public onDeleteClick(threadId: string, event: Event): void {
    event.stopPropagation();
    this.deleteThread.emit(threadId);
  }
}
